"use client";
import { useEffect, useRef, useState } from "react";
import { CheckIcon, CopyIcon } from "@soma/ui";
import { cn } from "@/lib/utils";

/**
 * Copies a value and says so for a moment. The confirmation is the whole point: the
 * clipboard gives no feedback of its own, and a hotkey is long enough that nobody can
 * tell by looking whether the copy worked.
 *
 * A failure - an insecure context, or a browser that refuses - leaves the icon alone
 * rather than showing a tick for something that did not happen.
 *
 * `bare` is the leaderboard-row form, the same as MineButton's: no border, shown on row
 * hover (always on touch screens, which have no hover), and kept visible while the tick
 * is up so the confirmation does not vanish with the pointer.
 */
export function CopyButton({
  value,
  label,
  copiedLabel,
  variant = "boxed",
  className,
}: {
  value: string;
  label: string;
  copiedLabel: string;
  variant?: "boxed" | "bare";
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? copiedLabel : label}
      title={label}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md",
        variant === "boxed"
          ? cn(
              "size-7 border border-line transition-colors",
              copied ? "border-ok/40 text-ok" : "text-fg-muted hover:border-fg-muted hover:text-fg",
            )
          : cn(
              "size-6 transition-[color,opacity] focus-visible:opacity-100",
              copied
                ? "text-ok"
                : "text-fg-faint opacity-0 group-hover/row:opacity-100 hover:text-fg [@media(hover:none)]:opacity-100",
            ),
        className,
      )}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
      {/* Announced to screen readers; the icon swap is the sighted half of the same news. */}
      <span className="sr-only" aria-live="polite">
        {copied ? copiedLabel : ""}
      </span>
    </button>
  );
}
